import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { BookingCard } from '../components/BookingCard';
import { useSeo } from '../lib/useSeo';
import { servicesData } from '../data/services';
import { ArrowRight } from 'lucide-react';
export function Sopotje() {
  const service = servicesData.find(s => s.id === 'sopotje')!;
  const otherServices = servicesData.filter(s => s.id !== service.id);
  useSeo({
    title: service.title,
    description: service.summary,
    path: service.link,
    image: service.image
  });
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-475">
      <Header data-id="element-476" />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-477">
        <div className="absolute inset-0 z-0" data-id="element-478">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-479" />
          <div className="absolute inset-0 bg-charcoal/40" data-id="element-480"></div>
        </div>

        <div className="relative z-10 text-center px-6 mt-16" data-id="element-481">
          <h1 className="text-4xl md:text-6xl font-serif text-white mb-4 drop-shadow-md" data-id="element-482">
            Guidance & Presence "Sopotje"
          </h1>
          <p className="text-white/90 font-light tracking-wide mt-2 drop-shadow-md" data-id="element-483">
            Walking along with you
          </p>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none" data-id="element-484">
          <WavyDivider fill="#F8F2E4" data-id="element-485" />
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-6" data-id="element-486">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-487">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-6" data-id="element-488">
            <p className="text-xl font-light text-charcoal-50 leading-relaxed italic" data-id="element-489">
              A gentle, grounded space for intuitive conversation, reflection,
              and emotional clarity. You are not guided from above, but
              accompanied side by side.
            </p>
            <p className="text-lg font-serif text-sage-700 italic" data-id="element-490">
              A presence that hears. A touch of words that resonate.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-491">
              I am a human being — intuitive, sensitive, and deeply experienced.
              My work is presence — warm, grounded, and without judgment. I
              don't offer diagnoses or instructions.{' '}
              <strong className="font-medium text-charcoal" data-id="element-492">
                I am not a psychologist. I am not a doctor.
              </strong>{' '}
              And that's exactly why I don't approach you as an "expert," but as
              a fellow human — with a heart that knows what it means to be truly
              present and to deeply listen.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-493">
              In my work, I don't offer solutions. I offer space. A space where
              you can pause, breathe, and hear yourself again. A space where you
              are not analyzed, but accepted.{' '}
              <strong className="font-medium text-charcoal" data-id="element-494">
                Sopotje means walking alongside
              </strong>{' '}
              — not leading from above, but being with you as you seek clarity,
              strength, or simply someone who truly hears you.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-495">
              When you're tired of overthinking, when you find yourself in
              emotional or life distress, or when you simply don't know where to
              turn — I offer a safe, confidential space. Sometimes, the greatest
              help is just that: someone who listens without judgment, who stays
              present with you in silence, or in gentle, honest conversation. I
              don't offer quick fixes. I offer{' '}
              <strong className="font-medium text-charcoal" data-id="element-496">
                presence that doesn't retreat.
              </strong>
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-497">
              My greatest teacher has been life itself — especially illness,
              which opened my eyes and taught me, sometimes painfully, what
              truly matters. It showed me that while we may not control
              circumstances, we can choose{' '}
              <strong className="font-medium text-charcoal" data-id="element-498">
                how present we are within them.
              </strong>{' '}
              Since then, I no longer carry just my own story — I carry a deep
              understanding of the journeys of others. And that's why I can now
              walk beside you — without judgment, with compassion, and with
              honest insight.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed border-l-2 border-sage pl-6 italic" data-id="element-499">
              Sopotje means: together we search for your clarity, your inner
              compass, your sense of true direction. My presence helps you hear
              yourself again.
            </p>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-500">
            <BookingCard price={service.price} priceDetail={service.priceDetail} duration="Approx. 60 minutes" format="In-person or Online" data-id="element-501" />
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="bg-cream-200 py-24 px-6 relative mt-12" data-id="element-502">
        <div className="absolute top-0 left-0 right-0 -translate-y-full pointer-events-none" data-id="element-503">
          <WavyDivider fill="#EFE6D5" position="top" data-id="element-504" />
        </div>

        <div className="max-w-7xl mx-auto" data-id="element-505">
          <h2 className="text-3xl font-serif text-charcoal mb-12 text-center" data-id="element-506">
            You might also like
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-507">
            {otherServices.map(s => <Link key={s.id} to={s.link} className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-md transition-all" data-id="element-508">
                <div className="aspect-[4/3] overflow-hidden" data-id="element-509">
                  <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-510" />
                </div>
                <div className="p-6" data-id="element-511">
                  <h3 className="font-serif text-xl text-charcoal mb-2" data-id="element-512">
                    {s.title}
                  </h3>
                  <span className="text-sage-700 text-sm flex items-center group-hover:text-sage-600 transition-colors" data-id="element-513">
                    Explore <ArrowRight size={14} className="ml-1" data-id="element-514" />
                  </span>
                </div>
              </Link>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-515" />
    </div>;
}